import React from "react";
import { useTranslation } from "@/lib/translate";
import { VADModeToggle } from "./VADModeToggle";
import { useAppStore } from "@/hooks/useAppStore";
import { Square } from "lucide-react";

interface RecordingOverlayProps {
  isRecording: boolean;
  waveBarRefs: React.RefObject<any>[];
  timerLabel: string;
  onStopRecording: () => void;
  onCancelRecording?: () => void;
}

export const RecordingOverlay: React.FC<RecordingOverlayProps> = ({
  isRecording,
  waveBarRefs,
  onStopRecording,
  onCancelRecording,
}) => {
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const isPresentationMode = useAppStore((state) => state.isPresentationMode);
  const t = useTranslation(preferredLanguage);

  if (!isRecording) return null;

  // Use only first 60 bars to match chotto-react
  const displayBars = waveBarRefs.slice(0, 60);

  return (
    <>
      {/* CSS Keyframes */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes fadeInWaveform {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes growButton {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>

      <div
        className="fixed inset-0 z-[1000] flex flex-col items-center justify-center"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          opacity: 0,
          animation: 'fadeIn 0.3s ease forwards',
        }}
      >
        {/* Wave Animation */}
        <div 
          className="flex items-center justify-center mb-12"
          style={{
            gap: '6px',
            height: '100px',
            width: '80%',
            maxWidth: '800px',
            opacity: 0,
            animation: 'fadeInWaveform 0.4s ease forwards',
          }}
        >
        {displayBars.map((ref, i) => (
          <div
            ref={ref}
            key={i}
            style={{ 
              width: '5px',
              minHeight: '10px',
              background: 'linear-gradient(180deg, #4DD0E1 0%, #00ACC1 100%)',
              borderRadius: '3px',
              transition: 'height 0.1s ease',
              boxShadow: '0 0 10px rgba(77, 208, 225, 0.5)',
            }}
          />
        ))}
      </div>
      {/* Button Container */}
      <div
        style={{
          position: 'fixed',
          bottom: '40px',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '20px',
          zIndex: 1001,
          animation: 'growButton 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
          flexWrap: 'wrap',       // Allow buttons to stack
          padding: '0 20px',      // Prevent edge touching on small screens
        }}
      >
        {/* VAD Toggle - Now on Left */}
        {/* Hide in Presentation Mode */}
        {!isPresentationMode && (
          <VADModeToggle onToggle={(enabled) => {
            if (enabled) {
              if (onCancelRecording) {
                onCancelRecording();
              } else {
                onStopRecording();
              }
            }
          }} />
        )}

        {/* Stop Recording Button - Now on Right */}
        <button
          onClick={onStopRecording}
          style={{
            minWidth: '220px', // Enforce equal width
            whiteSpace: 'nowrap',
            padding: '18px 20px', // Reduced side padding since width is fixed
            background: '#ff4444',
            border: 'none',
            borderRadius: '50px',
            color: '#fff',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            boxShadow: '0 8px 24px rgba(255, 68, 68, 0.4)',
            display: 'flex',           // Ensure centering
            justifyContent: 'center',  // Ensure centering
            alignItems: 'center',      // Ensure centering
            gap: '8px',                // Space between icon and text
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#ff2222';
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 12px 32px rgba(255, 68, 68, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#ff4444';
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 68, 68, 0.4)';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'scale(0.98)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
        >
          <Square className="h-5 w-5" fill="white" />
          <span>{t("Stop Recording")}</span>
        </button>
      </div>
      </div>
    </>
  );
};
